import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn, GridRow } from '../components/data/DataGrid';
import useAuthStore from '../store/authStore';
import { Location } from '../types/location';
import { useTheme } from '../contexts/ThemeContext';
import LocationImportModal from '../components/location/LocationImportModal';
import { generateLocationImportTemplate, downloadCSVFile } from '../utils/locationCsvTemplateGenerator';
import '../components/Modal.css';

interface Organization {
  id: number;
  name: string;
}

const LocationManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const { theme } = useTheme();
  const [locations, setLocations] = useState<Location[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [userOrganizationName, setUserOrganizationName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Location | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    latitude: '',
    longitude: '',
    organization_id: ''
  });
  
  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    fetchLocations();
    fetchOrganizations();
    fetchUserOrganization();
  }, []);

  const fetchUserOrganization = async () => {
    try {
      // Only fetch for organization admins (not super admins)
      if (user?.is_super_admin || !user?.organization_id) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/locations/user/organization`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.name) {
          setUserOrganizationName(data.data.name);
        }
      }
    } catch (err) {
      console.error('Error fetching user organization:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch locations: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setLocations(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch locations');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      // Only super admins need to fetch organizations list
      if (!user?.is_super_admin) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setOrganizations(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.name) {
        throw new Error('Location name is required');
      }

      let organizationId = formData.organization_id ? parseInt(formData.organization_id) : undefined;

      // For organization admins, use their organization_id
      if (!user?.is_super_admin && user?.organization_id) {
        organizationId = user.organization_id;
      }

      const payload = {
        name: formData.name,
        code: formData.code || undefined,
        address: formData.address || undefined,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        organization_id: organizationId
      };

      const url = editingLocation 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/locations/${editingLocation.id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/locations`;
      
      const method = editingLocation ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to ${editingLocation ? 'update' : 'create'} location`);
      }

      if (data.success) {
        setSuccess(`Location "${formData.name}" ${editingLocation ? 'updated' : 'created'} successfully!`);
        setFormData({ name: '', code: '', address: '', latitude: '', longitude: '', organization_id: '' });
        setShowCreateForm(false);
        setEditingLocation(null);
        fetchLocations();
      } else {
        throw new Error(data.message || `Failed to ${editingLocation ? 'update' : 'create'} location`);
      }
    } catch (err: any) {
      setError(err.message);
      console.error(`Error ${editingLocation ? 'updating' : 'creating'} location:`, err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteLocation = async (locationId: number) => {
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/locations/${locationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete location');
      }

      setSuccess('Location deleted successfully!');
      setShowDeleteConfirm(null);
      fetchLocations();
    } catch (err: any) {
      setError(err.message || 'Failed to delete location');
      setShowDeleteConfirm(null);
    }
  };

  const handleDeleteAllLocations = async () => {
    setError(null);
    setSuccess(null);
    setDeletingAll(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/locations/all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete all locations');
      }

      setSuccess(`Successfully deleted ${data.deletedCount} locations!`);
      setShowDeleteAllConfirm(false);
      fetchLocations();
    } catch (err: any) {
      setError(err.message || 'Failed to delete all locations');
      setShowDeleteAllConfirm(false);
    } finally {
      setDeletingAll(false);
    }
  };

  const startEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      code: location.code || '',
      address: location.address || '',
      latitude: location.latitude !== undefined ? String(location.latitude) : '',
      longitude: location.longitude !== undefined ? String(location.longitude) : '',
      organization_id: location.organization_id ? String(location.organization_id) : ''
    });
    setShowCreateForm(true);
  };

  const startCreate = () => {
    setFormData({
      name: '',
      code: '',
      address: '',
      latitude: '',
      longitude: '',
      // Auto-populate organization for org admins
      organization_id: (!user?.is_super_admin && user?.organization_id) 
        ? String(user.organization_id) 
        : ''
    });
    setShowCreateForm(true);
  };

  const cancelEdit = () => {
    setEditingLocation(null);
    setFormData({ name: '', code: '', address: '', latitude: '', longitude: '', organization_id: '' });
    setShowCreateForm(false);
  };

  const handleDownloadTemplate = () => {
    const template = generateLocationImportTemplate();
    downloadCSVFile(template, 'location-import-template.csv');
  };

  const handleImportComplete = () => {
    fetchLocations();
    setSuccess('Locations imported successfully!');
    setTimeout(() => setSuccess(null), 5000);
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="page-header">
            <h1>Location Management</h1>
            <p>
              {user?.is_super_admin 
                ? 'Manage locations across all organizations' 
                : 'Manage locations for your organization'
              }
            </p>
            
            <div className="header-actions">
              <button 
                className="secondary-button"
                onClick={handleDownloadTemplate}
                title="Download CSV template for bulk import"
              >
                📄 Download Template
              </button>
              {/* Delete all locations button - only for organization admins */}
              {!user?.is_super_admin && user?.organization_id && locations.length > 0 && (
                <button 
                  className="delete-all-locations-button"
                  onClick={() => setShowDeleteAllConfirm(true)}
                  title={`Delete all ${locations.length} locations for your organization`}
                >
                  <span className="button-icon">🗑️</span>
                  <span className="button-text">Delete All Locations</span>
                  <span className="button-count">({locations.length})</span>
                </button>
              )}
              <button 
                className="secondary-button"
                onClick={() => setShowImportModal(true)}
                title="Import locations from CSV file"
              >
                📁 Import from CSV
              </button>
              <button 
                className={`${showCreateForm ? 'secondary-button' : 'primary-button'}`}
                onClick={() => showCreateForm ? setShowCreateForm(false) : startCreate()}
                disabled={creating}
              >
                {showCreateForm ? 'View Locations' : 'Create Location'}
              </button>
              <button 
                className="secondary-button"
                onClick={fetchLocations}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="alert-close">×</button>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="alert-close">×</button>
            </div>
          )}

          {showCreateForm ? (
            <div className="card">
              <div className="card-header">
                <h2>{editingLocation ? 'Edit Location' : 'Create New Location'}</h2>
              </div>
              <div className="card-content">
                <form onSubmit={handleCreateLocation}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Location Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Downtown Showroom"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="code">Location Code</label>
                      <input
                        type="text"
                        id="code"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        placeholder="e.g., DTN001"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="e.g., 123 Main St, City, State 12345"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="latitude">Latitude</label>
                      <input
                        type="number"
                        id="latitude"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        placeholder="e.g., 51.5074"
                        step="any"
                        min="-90"
                        max="90"
                      />
                      <small style={{ color: theme === 'light' ? 'rgba(100, 116, 139, 0.8)' : '#888', fontSize: '11px' }}>
                        Optional: GPS latitude for map display (-90 to 90)
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="longitude">Longitude</label>
                      <input
                        type="number"
                        id="longitude"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder="e.g., -0.1278"
                        step="any"
                        min="-180"
                        max="180"
                      />
                      <small style={{ color: theme === 'light' ? 'rgba(100, 116, 139, 0.8)' : '#888', fontSize: '11px' }}>
                        Optional: GPS longitude for map display (-180 to 180)
                      </small>
                    </div>
                  </div>

                  {user?.is_super_admin ? (
                    <div className="form-group">
                      <label htmlFor="organization_id">Organization *</label>
                      <select
                        id="organization_id"
                        name="organization_id"
                        value={formData.organization_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Organization</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : user?.organization_id && (
                    <div className="form-group">
                      <label>Organization</label>
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: theme === 'light' ? 'rgba(248, 250, 252, 0.9)' : '#2a2a2a', 
                        border: theme === 'light' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid #444', 
                        borderRadius: '8px',
                        color: theme === 'light' ? 'rgba(15, 23, 42, 0.9)' : '#ccc'
                      }}>
                        {userOrganizationName || 'Your Organization'}
                      </div>
                      <small style={{ 
                        color: theme === 'light' ? 'rgba(100, 116, 139, 0.8)' : '#888', 
                        fontSize: '12px' 
                      }}>
                        Organization admins can only create locations in their assigned organization
                      </small>
                    </div>
                  )}

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="primary-button"
                      disabled={creating}
                    >
                      {creating ? (editingLocation ? 'Updating...' : 'Creating...') : (editingLocation ? 'Update Location' : 'Create Location')}
                    </button>
                    <button 
                      type="button" 
                      className="secondary-button"
                      onClick={editingLocation ? cancelEdit : () => setShowCreateForm(false)}
                      disabled={creating}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header">
                <h2>Locations ({locations.length})</h2>
              </div>
              <div className="card-content">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading locations...</p>
                  </div>
                ) : locations.length === 0 ? (
                  <div className="empty-state">
                    <h3>No Locations Yet</h3>
                    <p>Create your first location to get started.</p>
                    <button 
                      className="primary-button"
                      onClick={startCreate}
                    >
                      Create First Location
                    </button>
                  </div>
                ) : (
                  <DataGrid
                    columns={[
                      {
                        field: 'id',
                        headerName: 'ID',
                        width: 60,
                        type: 'number'
                      },
                      {
                        field: 'name',
                        headerName: 'Location Name',
                        width: 200,
                        renderCell: (row: GridRow) => (
                          <div>
                            <strong>{row.name}</strong>
                            {row.code && (
                              <div style={{ fontSize: '12px', color: '#888' }}>
                                Code: {row.code}
                              </div>
                            )}
                          </div>
                        )
                      },
                      {
                        field: 'address',
                        headerName: 'Address',
                        width: 250,
                        renderCell: (row: GridRow) => (
                          <span>{row.address || '-'}</span>
                        )
                      },
                      {
                        field: 'organizationName',
                        headerName: 'Organization',
                        width: 180,
                        renderCell: (row: GridRow) => (
                          <span>{row.organizationName || 'Unassigned'}</span>
                        )
                      },
                      {
                        field: 'created_at',
                        headerName: 'Created',
                        width: 120,
                        type: 'date',
                        renderCell: (row: GridRow) => (
                          <span>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}</span>
                        )
                      },
                      {
                        field: 'actions',
                        headerName: 'Actions',
                        width: 200,
                        renderCell: (row: GridRow) => (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button 
                              className="secondary-button"
                              onClick={() => startEdit(row as Location)}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              Edit
                            </button>
                            <button 
                              className="delete-button"
                              onClick={() => setShowDeleteConfirm(row as Location)}
                              style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                            >
                              Delete
                            </button>
                          </div>
                        )
                      }
                    ]}
                    rows={locations.map(location => ({
                      ...location
                    }))}
                    height="100%"
                    loading={loading}
                    className="locations-grid"
                  />
                )}
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Confirm Delete</h2>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete the location <strong>"{showDeleteConfirm.name}"</strong>?</p>
                  <p style={{ color: '#ff6b6b', fontSize: '14px' }}>
                    ⚠️ This action cannot be undone. All data associated with this location may be affected.
                  </p>
                </div>
                <div className="modal-actions">
                  <button 
                    className="secondary-button"
                    onClick={() => setShowDeleteConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteLocation(showDeleteConfirm.id)}
                  >
                    Delete Location
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete All Locations Confirmation Modal */}
          {showDeleteAllConfirm && (
            <div className="modal-overlay" onClick={() => setShowDeleteAllConfirm(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>⚠️ Confirm Delete All Locations</h2>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete <strong>ALL {locations.length} locations</strong> for your organization?</p>
                  <p style={{ color: '#ff6b6b', fontSize: '14px', fontWeight: 'bold' }}>
                    🚨 DANGER: This action cannot be undone!
                  </p>
                  <p style={{ color: '#ff6b6b', fontSize: '14px' }}>
                    All location data, associations, and related records may be permanently lost.
                  </p>
                  <p style={{ fontSize: '14px' }}>
                    Organization: <strong>{userOrganizationName || 'Your Organization'}</strong>
                  </p>
                </div>
                <div className="modal-actions">
                  <button 
                    className="secondary-button"
                    onClick={() => setShowDeleteAllConfirm(false)}
                    disabled={deletingAll}
                  >
                    Cancel
                  </button>
                  <button 
                    className="delete-button"
                    onClick={handleDeleteAllLocations}
                    disabled={deletingAll}
                  >
                    {deletingAll ? 'Deleting...' : `Delete All ${locations.length} Locations`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Location Import Modal */}
          <LocationImportModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            onImportComplete={handleImportComplete}
            organizationId={user?.organization_id}
          />
        </div>
      </div>
    </div>
  );
};

export default LocationManagementPage;